/**
 * Store Zustand para filtros de Documentos
 * Inclui toggle de tipos de documentos ativos
 */
import { create } from 'zustand';

// Tipos de documentos padrão (ativos por padrão)
export const DEFAULT_DOC_TYPES = [
  'AVCB - Auto de Vistoria do Corpo de Bombeiros',
  'Alvará de Funcionamento',
  'Licença Sanitária',
  'Licença Ambiental',
  'Licença Publicidade',
];

interface DocumentosFiltersState {
  // Filtros de Regional/UF
  regional: string | null;
  uf: string | null;
  
  // Tipos de documentos ativos (toggle buttons)
  activeDocTypes: string[];
  
  // Lista completa de tipos disponíveis
  availableDocTypes: string[];
  
  // Actions
  setRegional: (regional: string | null) => void;
  setUF: (uf: string | null) => void;
  toggleDocType: (docType: string) => void;
  setActiveDocTypes: (docTypes: string[]) => void;
  setAvailableDocTypes: (docTypes: string[]) => void;
  selectAllTypes: () => void;
  resetToDefaultTypes: () => void;
  clearFilters: () => void;
  
  // Helper para montar query params
  getFilterParams: () => Record<string, string>;
}

export const useDocumentosFilters = create<DocumentosFiltersState>((set, get) => ({
  // Estado inicial
  regional: null,
  uf: null,
  activeDocTypes: [...DEFAULT_DOC_TYPES],
  availableDocTypes: [...DEFAULT_DOC_TYPES],
  
  // Actions
  setRegional: (regional) => set({ regional, uf: null }),
  setUF: (uf) => set({ uf }),
  
  toggleDocType: (docType) => {
    const { activeDocTypes } = get();
    const isActive = activeDocTypes.includes(docType);
    
    if (isActive) {
      // Se só tem um ativo, não permite desativar
      if (activeDocTypes.length === 1) return;
      set({ activeDocTypes: activeDocTypes.filter(t => t !== docType) });
    } else {
      set({ activeDocTypes: [...activeDocTypes, docType] });
    }
  },
  
  setActiveDocTypes: (docTypes) => set({ activeDocTypes: docTypes }),
  
  setAvailableDocTypes: (docTypes) => set({ availableDocTypes: docTypes }),
  
  selectAllTypes: () => {
    const { availableDocTypes } = get();
    set({ activeDocTypes: [...availableDocTypes] });
  },
  
  resetToDefaultTypes: () => set({ activeDocTypes: [...DEFAULT_DOC_TYPES] }),
  
  clearFilters: () => set({ 
    regional: null, 
    uf: null, 
    activeDocTypes: [...DEFAULT_DOC_TYPES] 
  }),
  
  // Helper para montar query params
  getFilterParams: () => {
    const state = get();
    const params: Record<string, string> = {};
    
    if (state.regional) params.regional = state.regional;
    if (state.uf) params.uf = state.uf;
    if (state.activeDocTypes.length > 0) {
      params.docTypes = state.activeDocTypes.join(',');
    }
    
    return params;
  },
}));
